// Recommendation Service
// Uses transformers.js and user behavior analytics for ML-based job recommendations

import { pipeline } from '@xenova/transformers';
import { getUserBehavior, getUserCompletedJobs } from './userBehaviorService';
import { db } from '../config/firebase';
import { ref, get } from 'firebase/database';

let featureExtractor = null;

/**
 * Initialize ML feature extractor
 */
const initializeExtractor = async () => {
  if (!featureExtractor) {
    try {
      featureExtractor = await pipeline('feature-extraction', 'Xenova/all-MiniLM-L6-v2');
      console.log('%c[ML] Feature extractor initialized for recommendations', 'background: #4CAF50; color: white; padding: 5px;');
    } catch (error) {
      console.warn('%c[ML] Failed to initialize feature extractor', 'background: #FF9800; color: white; padding: 5px;', error);
    }
  }
  return featureExtractor;
};

/**
 * Calculate cosine similarity between two vectors
 */
const cosineSimilarity = (vecA, vecB) => {
  if (!vecA || !vecB || vecA.length !== vecB.length) {
    return 0;
  }

  let dotProduct = 0;
  let normA = 0;
  let normB = 0;

  for (let i = 0; i < vecA.length; i++) {
    dotProduct += vecA[i] * vecB[i];
    normA += vecA[i] * vecA[i];
    normB += vecB[i] * vecB[i];
  }

  normA = Math.sqrt(normA);
  normB = Math.sqrt(normB);

  if (normA === 0 || normB === 0) return 0;
  return dotProduct / (normA * normB);
};

/**
 * Extract semantic features from job text
 */
const extractJobFeatures = async (jobData) => {
  const extractor = await initializeExtractor();
  if (!extractor) return null;

  try {
    const jobText = `${jobData.title} ${(jobData.skills || []).join(' ')} ${jobData.description || ''} ${jobData.type}`;
    const output = await extractor(jobText, { pooling: 'mean', normalize: true });
    return Array.from(output.data);
  } catch (error) {
    console.warn('Error extracting job features:', error);
    return null;
  }
};

/**
 * Calculate behavior-based match score
 */
const calculateBehaviorScore = (jobData, userBehavior) => {
  if (!userBehavior) return 0;

  let score = 0;
  const weights = {
    skillMatch: 0.35,
    typeMatch: 0.20,
    locationMatch: 0.15,
    experienceMatch: 0.15,
    completedSkillBoost: 0.15
  };

  // Skill matching
  const preferredSkills = userBehavior.preferredSkills || [];
  const jobSkills = jobData.skills || [];
  const skillMatches = jobSkills.filter(skill =>
    preferredSkills.some(pSkill =>
      pSkill.toLowerCase() === skill.toLowerCase()
    )
  ).length;
  const skillScore = jobSkills.length > 0
    ? Math.min(skillMatches / jobSkills.length, 1)
    : 0;
  score += skillScore * weights.skillMatch;

  // Type matching
  const typeScore = (userBehavior.preferredTypes || []).includes(jobData.type) ? 1 : 0.3;
  score += typeScore * weights.typeMatch;

  // Location matching
  const locationScore = (userBehavior.preferredLocations || []).includes(jobData.location) ? 1 : 0.5;
  score += locationScore * weights.locationMatch;

  // Experience level matching
  const experienceScore = (userBehavior.experienceLevels || []).includes(jobData.experience) ? 1 : 0.6;
  score += experienceScore * weights.experienceMatch;

  // Completed skills boost (important!)
  const completedSkills = userBehavior.completedSkills || [];
  const completedSkillMatches = jobSkills.filter(skill =>
    completedSkills.some(cSkill =>
      cSkill.toLowerCase() === skill.toLowerCase()
    )
  ).length;
  const completedSkillScore = jobSkills.length > 0
    ? Math.min(completedSkillMatches / jobSkills.length, 1)
    : 0;
  score += completedSkillScore * weights.completedSkillBoost;

  return Math.min(score, 1);
};

/**
 * Get recommendations based on user behavior
 * Returns array of {jobId, scoreBreakdown} for recommended jobs
 */
export const getRecommendations = async (userId, allJobs, userPreferences = null) => {
  try {
    const userBehavior = await getUserBehavior(userId);
    console.log('[Recommendations] User behavior:', userBehavior);

    // If no behavior data, provide some default recommendations based on job quality
    if (!userBehavior || userBehavior.appliedCount === 0) {
      console.log('[Recommendations] No user behavior, returning quality-based recommendations');
      // Return top jobs by rating/popularity
      return allJobs
        .sort((a, b) => (b.ratingScore || 0) - (a.ratingScore || 0))
        .slice(0, 4)
        .map(job => ({
          job: job,
          jobId: job.id,
          score: 65,
          scoreBreakdown: {
            behavior: 0,
            semantic: 0
          },
          jobTitle: job.title,
          matchReasons: ['Popular job', 'Recommended for you']
        }));
    }

    const recommendations = [];
    const extractor = await initializeExtractor();

    for (const job of allJobs) {
      // Skip jobs user has already applied for
      const alreadyApplied = (userBehavior.applicationHistory || [])
        .some(app => app.jobId === job.id);
      if (alreadyApplied) continue;

      // Calculate behavior-based score
      const behaviorScore = calculateBehaviorScore(job, userBehavior);

      // If ML extractor available, calculate semantic similarity
      let semanticScore = 0;
      if (extractor) {
        try {
          const jobFeatures = await extractJobFeatures(job);
          const userPreferenceText = [
            ...(userBehavior.preferredSkills || []),
            ...(userBehavior.preferredTypes || []),
            ...(userBehavior.completedSkills || [])
          ].join(' ');

          if (jobFeatures && userPreferenceText) {
            const userFeatures = Array.from(
              (await extractor(userPreferenceText, { pooling: 'mean', normalize: true })).data
            );
            semanticScore = cosineSimilarity(jobFeatures, userFeatures);
          }
        } catch (error) {
          console.warn('Error calculating semantic score:', error);
        }
      }

      // Combine scores: 60% behavior, 40% semantic (lowered threshold for better recommendations)
      const finalScore = (behaviorScore * 0.6) + (semanticScore * 0.4);

      if (finalScore > 0.25) { // Lowered threshold from 0.3 to 0.25 for better recommendations
        const matchReasons = generateMatchReasons(userBehavior, job);
        recommendations.push({
          job: job,
          jobId: job.id,
          score: Math.round(finalScore * 100),
          scoreBreakdown: {
            behavior: Math.round(behaviorScore * 100),
            semantic: Math.round(semanticScore * 100)
          },
          jobTitle: job.title,
          matchReasons: matchReasons
        });
      }
    }

    // Sort by score descending and return top 5
    const sorted = recommendations
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
    
    console.log('[Recommendations] Found', sorted.length, 'recommendations');
    return sorted;
  } catch (error) {
    console.error('Error generating recommendations:', error);
    return [];
  }
};

/**
 * Generate human-readable reasons for recommendation
 */
const generateMatchReasons = (userBehavior, job) => {
  const reasons = [];

  if ((userBehavior.completedSkills || []).some(skill =>
    (job.skills || []).some(jSkill => jSkill.toLowerCase() === skill.toLowerCase())
  )) {
    reasons.push('Matches your completed skills');
  }

  if ((userBehavior.preferredSkills || []).some(skill =>
    (job.skills || []).some(jSkill => jSkill.toLowerCase() === skill.toLowerCase())
  )) {
    reasons.push('Matches your preferred skills');
  }

  if ((userBehavior.preferredTypes || []).includes(job.type)) {
    reasons.push('Your preferred job type');
  }

  if ((userBehavior.preferredLocations || []).includes(job.location)) {
    reasons.push('In your preferred location');
  }

  // Return array of reasons (up to 2)
  return reasons.length > 0 ? reasons.slice(0, 2) : ['Recommended for you'];
};
